import api from "./api";

export type ReportType = "comprehensive" | "sales-summary";

export interface ReportFilter {
  startDate: string;
  endDate: string;
  branchId?: string;
  salesPointId?: string;
  reportType: ReportType;
}

export interface ComprehensiveReportSummary {
  totalSales: number;
  totalSalesAmount: number;
  totalTaxAmount: number;
  totalDiscountAmount: number;
  totalPurchases: number;
  totalPurchaseAmount: number;
  totalPurchaseTax: number;
  totalInventoryItems: number;
  lowStockItemsCount: number;
  totalInventoryValue: number;
  totalTransactions: number;
  totalTransactionAmount: number;
  creditTransactionCount: number;
  debitTransactionCount: number;
}

export interface ComprehensiveReport {
  dateRange: { startDate: string; endDate: string };
  branch: { id: string; name: string } | null;
  salesPoint: { id: string; name: string } | null;
  summary: ComprehensiveReportSummary;
  inventory: {
    lowStockItems: Array<{
      id: string;
      availableQuantity: number;
      stockQuantity: number;
      minStockAlert?: number;
      reorderLevel?: number;
      product?: { id: string; name: string; sku?: string };
    }>;
  };
  transactions: Array<{
    id: string;
    amount: number;
    type: string;
    description?: string | null;
    transactionDate: string;
    createdAt: string;
    branch?: { name: string };
    cardHolder?: { holderName: string; phone?: string };
  }>;
}

export interface SalesSummaryReport {
  dateRange: { startDate: string; endDate: string };
  salesByPaymentMethod: Array<{
    paymentMethod: string;
    transactionCount: string | number;
    totalAmount: string | number;
  }>;
  dailySales: Array<{
    date: string;
    transactionCount: string | number;
    totalAmount: string | number;
    totalTax?: string | number;
    totalDiscount?: string | number;
  }>;
  topSellingProducts: Array<{
    productId?: string;
    "product.name"?: string;
    "product.sku"?: string;
    totalQuantity: string | number;
    totalRevenue: string | number;
    product?: { name: string; sku?: string };
  }>;
}

export type ReportResult = ComprehensiveReport | SalesSummaryReport;

export interface SalesReportRow {
  id: string;
  saleNumber: string;
  totalAmount: number;
  netAmount: number;
  taxAmount?: number;
  discountAmount?: number;
  paymentMethod: string;
  status: string;
  notes?: string | null;
  createdAt: string;
  branch?: { id: string; name: string };
  salesPoint?: { id: string; name: string };
  cardHolder?: { id: string; holderName: string } | null;
  creator?: { id: string; name: string } | null;
  completedAt?: string;
  orderType?: string;
  tableNumber?: string | null;
  customerName?: string | null;
  creditNotes?: Array<{
    id: string;
    creditNoteNumber: string;
    netAmount: number;
    reason?: string | null;
    createdAt?: string;
  }>;
  saleItems?: Array<{
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    netAmount?: number;
    gstPercentage?: number;
    isTaxInclusive?: boolean;
    notes?: string | null;
    product?: { id: string; name: string; sku?: string };
  }>;
}

export interface SalesReportData {
  dateRange: { startDate: string; endDate: string };
  scope: {
    branchId: string | null;
    salesPointId: string | null;
    createdBy?: string | null;
    isSuperAdmin: boolean;
    isFieldStaff?: boolean;
  };
  branch: { id: string; name: string } | null;
  salesPoint: { id: string; name: string } | null;
  summary: {
    totalSales: number;
    totalAmount: number;
    totalNet: number;
    totalTax: number;
    totalDiscount: number;
  };
  cancelledSummary?: {
    totalCancelled: number;
    cancelledAmount: number;
    cancelledNet: number;
  };
  refundedSummary?: {
    totalRefunded: number;
    refundedAmount: number;
    refundedNet: number;
  };
  byBranch: Array<{
    branchId: string;
    branchName: string;
    saleCount: number;
    totalNet: number;
  }>;
  bySalesPoint: Array<{
    salesPointId: string;
    salesPointName: string;
    branchName: string;
    saleCount: number;
    totalNet: number;
  }>;
  byPaymentMethod: Array<{
    paymentMethod: string;
    saleCount: number;
    totalNet: number;
  }>;
  sales: SalesReportRow[];
  pagination: { pageNo: number; pageSize: number; totalRecords: number };
}

export interface RechargesReportRow {
  id: string;
  amount: number;
  paymentMethod: string;
  status: string;
  completedAt?: string | null;
  createdAt: string;
  notes?: string | null;
  cardHolder?: { id: string; holderName: string; phone?: string } | null;
  branch?: { id: string; name: string };
  salesPoint?: { id: string; name: string };
}

export interface RechargesReportData {
  dateRange: { startDate: string; endDate: string };
  scope: {
    branchId: string | null;
    salesPointId: string | null;
    createdBy: string | null;
    isFieldStaff: boolean;
  };
  summary: {
    totalRecharges: number;
    totalAmount: number;
  };
  byPaymentMethod: Array<{
    paymentMethod: string;
    rechargeCount: number;
    totalAmount: number;
  }>;
  recharges: RechargesReportRow[];
  pagination: { pageNo: number; pageSize: number; totalRecords: number };
}

export type SalesReportSortField =
  | "createdAt"
  | "saleNumber"
  | "branch"
  | "salesPoint"
  | "paymentMethod"
  | "holderName"
  | "soldBy"
  | "netAmount"
  | "totalAmount";

export type RechargeReportSortField =
  | "createdAt"
  | "holderName"
  | "phone"
  | "paymentMethod"
  | "amount";

export type ReportSortOrder = "ASC" | "DESC";

export interface ReportDateRangeFilter {
  startDate: string;
  endDate: string;
  branchId?: string;
  salesPointId?: string;
  pageNo?: number;
  pageSize?: number;
}

export interface SalesReportFilter extends ReportDateRangeFilter {
  /** Cashier / operator who created the sale */
  userId?: string;
  sortBy?: SalesReportSortField;
  sortOrder?: ReportSortOrder;
  fyCode?: string;
  financialYearId?: string;
}

export interface RechargesReportFilter extends ReportDateRangeFilter {
  sortBy?: RechargeReportSortField;
  sortOrder?: ReportSortOrder;
}

const reportAPI = {
  getSalesReport: async (filters: SalesReportFilter): Promise<SalesReportData> => {
    const params: Record<string, string | number> = {
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
    if (filters.branchId) params.branchId = filters.branchId;
    if (filters.salesPointId) params.salesPointId = filters.salesPointId;
    if (filters.userId) params.userId = filters.userId;
    if (filters.pageNo) params.pageNo = filters.pageNo;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters.fyCode) params.fyCode = filters.fyCode;
    if (filters.financialYearId) params.financialYearId = filters.financialYearId;

    const response = await api.get("/reports/sales", { params });
    return response.data.data;
  },

  getRechargesReport: async (filters: RechargesReportFilter): Promise<RechargesReportData> => {
    const params: Record<string, string | number> = {
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
    if (filters.branchId) params.branchId = filters.branchId;
    if (filters.salesPointId) params.salesPointId = filters.salesPointId;
    if (filters.pageNo) params.pageNo = filters.pageNo;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;

    const response = await api.get("/reports/recharges", { params });
    return response.data.data;
  },

  getReport: async (filters: ReportFilter): Promise<ReportResult> => {
    const params: Record<string, string> = {
      reportType: filters.reportType,
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
    if (filters.branchId) params.branchId = filters.branchId;
    if (filters.salesPointId) params.salesPointId = filters.salesPointId;

    const response = await api.get("/reports", { params });
    return response.data.data;
  },

  getGstr1: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/gstr1", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getGstr1PortalExport: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/gstr1/portal-export", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getPurchaseRegister: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/purchase-register", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getItcRegister: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/itc-register", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getDayBook: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/day-book", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getPartyLedger: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/party-ledger", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getGstr3b: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/gstr3b", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getProfitLoss: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/profit-loss", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getTallyExport: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/tally-export", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getTallyPartiesExport: async () => {
    const response = await api.get("/reports/tally-parties-export");
    return response.data.data as {
      format: string;
      columns: string[];
      rows: Array<Record<string, unknown>>;
    };
  },

  getExpiryReport: async (filters: GstBooksFilter & { withinDays?: number }) => {
    const params = gstParams(filters);
    if (filters.withinDays) params.withinDays = String(filters.withinDays);
    const response = await api.get("/reports/expiry", { params });
    return response.data.data;
  },

  getStockLedger: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/stock-ledger", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getBalanceSheet: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/balance-sheet", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getTrialBalance: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/trial-balance", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getCashBook: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/cash-book", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getRefundRegister: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/refund-register", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getNfcLiability: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/nfc-liability", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getSlowDeadStock: async (
    filters: GstBooksFilter & { noSaleDays?: number }
  ) => {
    const params = gstParams(filters);
    if (filters.noSaleDays) params.noSaleDays = String(filters.noSaleDays);
    const response = await api.get("/reports/slow-dead-stock", { params });
    return response.data.data;
  },

  getOperatorSales: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/operator-sales", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getLowStockReport: async (
    filters: Pick<GstBooksFilter, "branchId" | "salesPointId"> & {
      alertType?: "all" | "low_stock" | "out_of_stock";
    }
  ) => {
    const params: Record<string, string> = {};
    if (filters.branchId) params.branchId = filters.branchId;
    if (filters.salesPointId) params.salesPointId = filters.salesPointId;
    if (filters.alertType) params.alertType = filters.alertType;
    const response = await api.get("/reports/low-stock", { params });
    return response.data.data;
  },

  getTableTurnaround: async (filters: GstBooksFilter) => {
    const response = await api.get("/reports/table-turnaround", {
      params: gstParams(filters),
    });
    return response.data.data;
  },

  getSalesReturns: async (params: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
    salesPointId?: string;
    search?: string;
    pageNo?: number;
    pageSize?: number;
  }) => {
    const response = await api.get("/sales/returns", { params });
    return response.data?.data || response.data;
  },
};

export interface GstBooksFilter {
  startDate: string;
  endDate: string;
  branchId?: string;
  salesPointId?: string;
  fyCode?: string;
  financialYearId?: string;
}

function gstParams(filters: GstBooksFilter): Record<string, string> {
  const params: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };
  if (filters.branchId) params.branchId = filters.branchId;
  if (filters.salesPointId) params.salesPointId = filters.salesPointId;
  if (filters.fyCode) params.fyCode = filters.fyCode;
  if (filters.financialYearId) params.financialYearId = filters.financialYearId;
  return params;
}

export default reportAPI;
